import { SharedSlice } from 'src/components/slice'
import { StateCreator } from 'zustand'
import {
  IItem,
  IItemSupplierVariant,
  ItemSupplierVariant,
  IPurchaseOrder,
  IPurchaseOrderCommunicationLog,
  Item,
  PaginationModel,
  PurchaseOrder,
} from '$models'
import { purchaseOrderService, stockCostService } from '$services'
import { enqueueLoadedSnackbar, exceptionMessageHandler, logger } from '$utils'
import { orderBy } from 'lodash'
import { PurchaseOrderStatus } from '$constants'
import { MAX_POLL_COUNT, pollDelays } from './constants'
import {
  OutstandingPurchaseOrdersUi,
  PurchaseOrderUi,
  PurchaseOrderUiLine,
} from './types'
import dayjs, { Dayjs } from 'dayjs'
import { updatePurchaseOrderLine } from './add/helpers/helper'
import useStockStore from '../../store'
import {
  convertPurchaseOrderToPurchaseOrderUi,
  convertPurchaseOrderUiToPurchaseOrder,
} from './helpers/helper'
import {
  FileResponse,
  SendPurchaseOrderResponse,
} from '@loadedhub/loaded-stock-api-sdk'
import { GridSortModel } from '@mui/x-data-grid-pro'
import { extractPaginationModel, extractSortModel } from '$components/common'
import { isCancelledError } from '@tanstack/react-query'
import { transformPredictedOrderLinesToPurchaseOrderLines } from '../predicted-order/helpers/helpers'

export interface PurchaseOrderSlice extends PurchaseOrderState {
  resetPurchaseOrderSlice: () => void

  getPurchaseOrdersPaginated(
    queryConditions?: PurchaseOrderQueryConditions
  ): Promise<void>

  deletePurchaseOrderLine: (lineId: string) => void

  setPurchaseOrderUi: (updatedPurchaseOrderUi: PurchaseOrderUi) => void

  setPurchaseOrderNotes: (updatedNotes: string) => void

  updatePurchaseOrderSupplierId: (updatedSupplierId: string) => void

  updatePurchaseOrderLineItem: (
    purchaseOrderLineId: string,
    updatedItemId: string,
    itemSupplierVariant?: IItemSupplierVariant
  ) => Promise<void>

  insertPurchaseOrderLine: (
    newItem: IItem,
    itemSupplierVariant?: IItemSupplierVariant
  ) => Promise<void>

  updatePurchaseOrderLineSupplier: (
    updatedSupplierId: string,
    currentPurchaseOrderLine: PurchaseOrderUiLine
  ) => Promise<void>

  updateQuantity: (
    updatedQuantity: number,
    purchaseOrderLineId: string
  ) => Promise<void>

  updateCode: (
    updatedCode: string,
    currentPurchaseOrderLine: PurchaseOrderUiLine
  ) => Promise<void>

  updateBrand: (
    updatedBrandId: string,
    currentPurchaseOrderLine: PurchaseOrderUiLine,
    updateForFutureOrders: boolean
  ) => Promise<void>

  updateUnit: (
    updatedUnitId: string,
    currentPurchaseOrderLine: PurchaseOrderUiLine,
    updateForFutureOrders: boolean
  ) => Promise<void>

  updatePurchaseOrderLineVariant: (
    lineId: string,
    variant: IItemSupplierVariant
  ) => void

  addVariantToLine: (
    lineId: string,
    variant: Omit<IItemSupplierVariant, 'id' | 'stockItemId'>
  ) => Promise<void>

  reorderRow: (lineId: string, targetIndex: number) => void

  updatePurchaseOrder: () => Promise<void>

  updateEstimatedCost: (
    updatedEstimatedCost: number,
    purchaseOrderLineId: string
  ) => void

  setEstimatedCostLoading: (
    purchaseOrderLineId: string,
    isLoading: boolean
  ) => void

  getEstimatedCost: (
    purchaseOrderLineId?: string,
    itemId?: string,
    unitRatio?: number,
    quantity?: number
  ) => Promise<void>

  getOutstandingPurchaseOrders: () => Promise<void>

  deletePurchaseOrder: (
    purchaseOrderId: string
  ) => Promise<IPurchaseOrder | null>

  populatePurchaseOrderUi: (purchaseOrder: IPurchaseOrder) => Promise<void>

  startOutstandingOrdersPolling: () => Promise<void>

  stopOutstandingOrdersPolling: () => void

  sendPurchaseOrderToSupplier: (
    purchaseOrderId: string,
    supplierEmail: string,
    updateSupplierEmail: boolean,
    emailSubject: string,
    companyName: string,
    additionalMessage: string
  ) => Promise<SendPurchaseOrderResponse | undefined>

  getPurchaseOrderCommunicationLogs: (
    purchaseOrderId: string
  ) => Promise<IPurchaseOrderCommunicationLog[]>

  downloadPurchaseOrderPdf: (
    purchaseOrderId: string
  ) => Promise<FileResponse | undefined>

  setPurchaseOrderLoading: (isLoading: boolean) => void

  populatePurchaseOrderFromPredictedOrder: () => void
}
interface PurchaseOrderState {
  purchaseOrderUi?: PurchaseOrderUi
  outstandingPurchaseOrders?: OutstandingPurchaseOrdersUi[]
  purchaseOrdersHistory: PurchaseOrder[]
  purchaseOrderPaginationModel: PaginationModel
  purchaseOrderSortModel: GridSortModel
}

export interface PurchaseOrderQueryConditions {
  start?: Dayjs
  end?: Dayjs
  referenceNumber?: string
  supplierName?: string
  page: number
  pageSize: number
  sort?: string
}

const defaultPurchaseOrderQueryConditions = (
  queryCondition: Partial<PurchaseOrderQueryConditions> = {}
) => {
  return {
    start: queryCondition.start,
    end: queryCondition.end,
    referenceNumber: queryCondition.referenceNumber,
    supplierName: queryCondition.supplierName,
    page: queryCondition.page ?? 0,
    pageSize: queryCondition.pageSize ?? 20,
    sort: queryCondition.sort,
  }
}

const initialOutstandingOrdersState: PurchaseOrderState = {
  purchaseOrderUi: {
    lines: [],
    notes: undefined,
  },
  outstandingPurchaseOrders: undefined,
  purchaseOrdersHistory: [],
  purchaseOrderPaginationModel: {
    page: 0,
    pageSize: 20,
    total: -1, // -1 means the total is unknown
  },
  purchaseOrderSortModel: [
    {
      field: 'datestampCreated',
      sort: 'desc',
    },
  ],
}

let pollTimeoutId: NodeJS.Timeout | null = null
let pollCount = 0

export const purchaseOrderSlice: StateCreator<
  PurchaseOrderSlice & SharedSlice,
  [],
  [],
  PurchaseOrderSlice
> = (set, get) => ({
  ...initialOutstandingOrdersState,

  resetPurchaseOrderSlice: () => set(initialOutstandingOrdersState),

  getPurchaseOrdersPaginated: async (queryConditions): Promise<void> => {
    try {
      const finalQueryConditions =
        defaultPurchaseOrderQueryConditions(queryConditions)
      set(() => ({ isLoading: true }))
      const response = await purchaseOrderService.getPurchaseOrdersHistory(
        finalQueryConditions
      )
      set(() => ({
        purchaseOrdersHistory: response.result,
        purchaseOrderPaginationModel: extractPaginationModel(response),
        purchaseOrderSortModel: extractSortModel(response),
      }))
    } catch (error) {
      if (isCancelledError(error)) {
        logger.debug('PurchaseOrders query was cancelled')
        // We do a return here as we don't want to update the state if the query was cancelled,
        // otherwise it would be an empty array until the last call finishes and return the values
        return
      }
      set(() => ({
        purchaseOrdersHistory: [],
      }))
      exceptionMessageHandler(error, 'Failed to retrieve purchase orders')
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  deletePurchaseOrderLine: (lineId) => {
    set((state) => {
      if (!state.purchaseOrderUi?.lines)
        return { purchaseOrderUi: state.purchaseOrderUi }

      const updatedLines = state.purchaseOrderUi.lines.filter(
        (line) => line.lineId !== lineId
      )

      return {
        purchaseOrderUi: {
          ...state.purchaseOrderUi,
          lines: updatedLines,
        },
      }
    })
  },

  setPurchaseOrderUi: (updatedPurchaseOrderUi) =>
    set(() => ({
      purchaseOrderUi: updatedPurchaseOrderUi,
    })),

  setPurchaseOrderNotes: (updatedNotes) => {
    set(() => ({
      purchaseOrderUi: {
        ...get().purchaseOrderUi,
        notes: updatedNotes,
      },
    }))
  },

  updatePurchaseOrderSupplierId: (updatedSupplierId) => {
    set(() => ({
      purchaseOrderUi: {
        ...get().purchaseOrderUi,
        supplierId: updatedSupplierId,
      },
    }))
  },

  updatePurchaseOrderLineItem: async (
    purchaseOrderLineId,
    updatedItemId,
    itemSupplierVariant
  ) => {
    const currentPurchaseOrderLine = get().purchaseOrderUi?.lines?.find(
      (line) => line.lineId === purchaseOrderLineId
    )

    if (!currentPurchaseOrderLine) return

    // Use state directly instead of async methods for better performance
    const { items, units, salesTax } = useStockStore.getState()

    const updatedItem = items && items[updatedItemId]

    if (!updatedItem?.id) return

    const unitId = itemSupplierVariant
      ? itemSupplierVariant?.unitId
      : updatedItem?.orderingUnitId

    const unitRatio =
      itemSupplierVariant && units
        ? units[itemSupplierVariant?.unitId ?? '']?.ratio
        : updatedItem?.orderingUnitRatio

    const supplierId = itemSupplierVariant
      ? itemSupplierVariant?.supplierId
      : updatedItem.defaultSupplierId

    const brandId = itemSupplierVariant
      ? itemSupplierVariant?.brandId
      : updatedItem?.defaultBrandId

    const updatedLine: PurchaseOrderUiLine = {
      ...currentPurchaseOrderLine,
      itemCode: itemSupplierVariant?.stockCode,
      itemId: updatedItem.id,
      itemSupplierId: itemSupplierVariant?.id,
      taxPercent:
        updatedItem?.globalSalesTaxSortOrder &&
        salesTax?.[updatedItem.globalSalesTaxSortOrder]?.rate,
      brandId: brandId,
      supplierId: supplierId,
      unitId: unitId,
      unitRatio: unitRatio,
      __reorder__: updatedItem.name,
      isEstimatedCostLoading: true,
    }

    // Make this call non-blocking
    get().getEstimatedCost(
      purchaseOrderLineId,
      updatedItem?.id,
      unitRatio,
      currentPurchaseOrderLine?.quantityOrdered
    )

    set((state) => {
      const lines = state.purchaseOrderUi?.lines
      if (!lines) return { purchaseOrderUi: state.purchaseOrderUi }

      // More efficient than .map updates only the matching line by index
      // Avoids rebuilding the entire array and maintains immutability for Zustand
      const index = lines.findIndex(
        (line) => line.lineId === updatedLine.lineId
      )
      if (index === -1) return { purchaseOrderUi: state.purchaseOrderUi }

      const updatedLines = [...lines]
      updatedLines[index] = updatedLine

      return {
        purchaseOrderUi: {
          ...state.purchaseOrderUi,
          lines: updatedLines,
        },
      }
    })
  },

  insertPurchaseOrderLine: async (newItem, itemSupplierVariant) => {
    if (!newItem.id) return

    const lineId = window.crypto.randomUUID()
    const quantityOrdered = 1

    // Use state directly instead of async methods for better performance
    const { units, salesTax } = useStockStore.getState()

    const brandId = itemSupplierVariant
      ? itemSupplierVariant?.brandId
      : newItem?.defaultBrandId

    const unitId = itemSupplierVariant
      ? itemSupplierVariant?.unitId
      : newItem?.orderingUnitId

    const unitRatio =
      itemSupplierVariant && units
        ? units[itemSupplierVariant?.unitId ?? '']?.ratio
        : newItem?.orderingUnitRatio

    const supplierId = itemSupplierVariant
      ? itemSupplierVariant?.supplierId
      : newItem.defaultSupplierId

    const newLine: PurchaseOrderUiLine = {
      lineId: lineId,
      itemId: newItem.id,
      __reorder__: newItem.name,
      supplierId: supplierId,
      itemSupplierId: itemSupplierVariant?.id,
      taxPercent:
        newItem?.globalSalesTaxSortOrder &&
        salesTax?.[newItem.globalSalesTaxSortOrder]?.rate,
      brandId: brandId,
      unitId: unitId,
      unitRatio: unitRatio,
      itemCode: itemSupplierVariant?.stockCode,
      quantityOrdered: quantityOrdered,
      isEstimatedCostLoading: true,
    }

    // Update state immediately for better UX
    set((state) => ({
      purchaseOrderUi: {
        ...state.purchaseOrderUi,
        lines: [...(state.purchaseOrderUi?.lines ?? []), newLine],
      },
    }))

    // Make this call non-blocking by not awaiting it
    get().getEstimatedCost(lineId, newItem.id, unitRatio, quantityOrdered)
  },

  updateBrand: async (
    updatedBrandId,
    currentPurchaseOrderLine,
    updateForFutureOrders
  ) => {
    if (
      currentPurchaseOrderLine.brandId === updatedBrandId ||
      !currentPurchaseOrderLine?.itemId
    )
      return

    const updatedLine: PurchaseOrderUiLine = {
      ...currentPurchaseOrderLine,
      brandId: updatedBrandId,
      ...(!updateForFutureOrders && {
        itemCode: undefined,
        itemSupplierId: undefined,
      }),
    }

    set((state) => {
      const currentUi = state.purchaseOrderUi
      const lines = currentUi?.lines ?? []

      const updatedLines = updatePurchaseOrderLine(lines, updatedLine)

      return {
        purchaseOrderUi: {
          ...currentUi,
          lines: updatedLines ?? lines,
        },
      }
    })

    if (updateForFutureOrders && currentPurchaseOrderLine?.itemSupplierId) {
      useStockStore
        .getState()
        .patchItemSupplierVariant(
          currentPurchaseOrderLine.itemSupplierId,
          currentPurchaseOrderLine.itemId,
          updatedLine.brandId,
          currentPurchaseOrderLine.unitId
        )
    }
  },

  updateCode: async (updatedCode, currentPurchaseOrderLine) => {
    if (
      currentPurchaseOrderLine.itemCode === updatedCode ||
      !currentPurchaseOrderLine?.itemId
    )
      return

    const updatedLine: PurchaseOrderUiLine = {
      ...currentPurchaseOrderLine,
      itemCode: updatedCode,
    }

    set((state) => {
      const currentUi = state.purchaseOrderUi
      const lines = currentUi?.lines ?? []

      const updatedLines = updatePurchaseOrderLine(lines, updatedLine)

      return {
        purchaseOrderUi: {
          ...currentUi,
          lines: updatedLines ?? lines,
        },
      }
    })
  },

  updateUnit: async (
    updatedUnitId,
    currentPurchaseOrderLine,
    updateForFutureOrders
  ) => {
    // Use state directly instead of async method for better performance
    const { units } = useStockStore.getState()

    if (
      currentPurchaseOrderLine.unitId === updatedUnitId ||
      !currentPurchaseOrderLine?.itemId ||
      !units
    )
      return

    const updatedUnit = units[updatedUnitId]

    const updatedLine: PurchaseOrderUiLine = {
      ...currentPurchaseOrderLine,
      unitId: updatedUnit.id,
      unitRatio: updatedUnit.ratio,
      ...(!updateForFutureOrders && {
        itemCode: undefined,
        itemSupplierId: undefined,
      }),
    }

    set((state) => {
      const currentUi = state.purchaseOrderUi
      const lines = currentUi?.lines ?? []

      const updatedLines = updatePurchaseOrderLine(lines, updatedLine)

      return {
        purchaseOrderUi: {
          ...currentUi,
          lines: updatedLines ?? lines,
        },
      }
    })

    if (updateForFutureOrders && currentPurchaseOrderLine?.itemSupplierId) {
      useStockStore
        .getState()
        .patchItemSupplierVariant(
          currentPurchaseOrderLine.itemSupplierId,
          currentPurchaseOrderLine.itemId,
          currentPurchaseOrderLine.brandId,
          updatedUnit.id
        )
    }
  },

  updatePurchaseOrderLineSupplier: async (
    updatedSupplierId,
    currentPurchaseOrderLine
  ) => {
    if (
      updatedSupplierId === currentPurchaseOrderLine.supplierId ||
      !currentPurchaseOrderLine.itemId
    )
      return

    // Use state directly instead of async methods for better performance
    const { items, units } = useStockStore.getState()

    const currentItem = items?.[currentPurchaseOrderLine.itemId]

    const itemSupplierVariant = currentItem?.suppliers?.find(
      (itemSupplier) =>
        itemSupplier.supplierId === updatedSupplierId &&
        itemSupplier.defaultForSupplier
    )

    const updatedLine: PurchaseOrderUiLine = {
      ...currentPurchaseOrderLine,
      supplierId: updatedSupplierId,
      itemSupplierId: itemSupplierVariant?.id,
      itemCode: itemSupplierVariant?.stockCode
        ? itemSupplierVariant.stockCode
        : '',
      unitId: itemSupplierVariant?.unitId
        ? itemSupplierVariant.unitId
        : currentItem?.orderingUnitId,
      unitRatio:
        itemSupplierVariant && units
          ? units[itemSupplierVariant?.unitId ?? '']?.ratio
          : currentItem?.orderingUnitRatio,
      brandId: itemSupplierVariant?.brandId
        ? itemSupplierVariant.brandId
        : currentItem?.defaultBrandId,
    }

    set((state) => {
      const currentUi = state.purchaseOrderUi
      const lines = currentUi?.lines ?? []

      const updatedLines = updatePurchaseOrderLine(lines, updatedLine)

      return {
        purchaseOrderUi: {
          ...currentUi,
          lines: updatedLines ?? lines,
        },
      }
    })
  },

  updateQuantity: async (updatedQuantity, purchaseOrderLineId) => {
    const purchaseOrderLine = get().purchaseOrderUi?.lines?.find(
      (line) => line.lineId === purchaseOrderLineId
    )

    if (!purchaseOrderLine) return

    const updatedLine: PurchaseOrderUiLine = {
      ...purchaseOrderLine,
      quantityOrdered: updatedQuantity,
    }

    set((state) => {
      const currentUi = state.purchaseOrderUi
      const lines = currentUi?.lines ?? []

      const updatedLines = updatePurchaseOrderLine(lines, updatedLine)

      return {
        purchaseOrderUi: {
          ...currentUi,
          lines: updatedLines ?? lines,
        },
      }
    })
  },

  updatePurchaseOrderLineVariant: (lineId, variant) => {
    // Use state directly instead of async methods for better performance
    const { units, brands } = useStockStore.getState()

    if (!units || !brands) return

    set((state) => {
      const currentUi = state.purchaseOrderUi
      const lines = currentUi?.lines ?? []

      const currentLine = lines.find((line) => line.lineId === lineId)
      if (!currentLine) return { purchaseOrderUi: currentUi }

      const updatedLine: PurchaseOrderUiLine = {
        ...currentLine,
        itemCode: variant.stockCode,
        itemSupplierId: variant.id,
        brandId: variant.brandId,
        brandName: variant.brandId ? brands[variant.brandId]?.name : undefined,
        unitId: variant.unitId,
        unitName: variant.unitId ? units[variant.unitId]?.name : undefined,
        unitRatio: variant.unitId ? units[variant.unitId]?.ratio : undefined,
      }

      const updatedLines = updatePurchaseOrderLine(lines, updatedLine)

      return {
        purchaseOrderUi: {
          ...currentUi,
          lines: updatedLines ?? lines,
        },
      }
    })
  },

  addVariantToLine: async (lineId, variant) => {
    const stockStore = useStockStore.getState()
    const { items, units, brands, updateItem } = stockStore
    const currentLine = get().purchaseOrderUi?.lines?.find(
      (line) => line.lineId === lineId
    )

    if (!currentLine?.itemId || !items || !units || !brands) return

    const currentItem = items[currentLine.itemId]
    if (!currentItem) return

    // Store the original item for potential rollback
    const originalItem = currentItem

    // Create the new variant object to add to the item's suppliers array
    const newVariantData: IItemSupplierVariant = {
      ...variant,
      stockItemId: currentItem.id!,
      defaultForSupplier: false,
    }

    const newVariant = new ItemSupplierVariant(newVariantData)

    // Optimistically update the items state first
    const optimisticItem = new Item({
      ...currentItem,
      suppliers: [...(currentItem.suppliers ?? []), newVariant],
    })

    await stockStore.updateItemState(optimisticItem)

    try {
      // Call the API to persist the change
      const updatedItem = await updateItem(optimisticItem)

      if (!updatedItem) {
        // Rollback on failure
        await stockStore.updateItemState(originalItem)
        return
      }
    } catch (error) {
      // Rollback on error
      await stockStore.updateItemState(originalItem)
      exceptionMessageHandler(error, 'Failed to add variant')
    }
  },

  reorderRow: (lineId, targetIndex) => {
    set((state) => {
      const lines = [...(state.purchaseOrderUi?.lines ?? [])]

      const currentIndex = lines.findIndex((line) => line.lineId === lineId)
      if (currentIndex === -1 || targetIndex < 0 || targetIndex >= lines.length)
        return { purchaseOrderUi: state.purchaseOrderUi }

      // Remove the row
      const [movedLine] = lines.splice(currentIndex, 1)

      // Insert at new index
      lines.splice(targetIndex, 0, movedLine)

      return {
        purchaseOrderUi: {
          ...state.purchaseOrderUi,
          lines: lines,
        },
      }
    })
  },

  updatePurchaseOrder: async (): Promise<void> => {
    const { purchaseOrderUi, outstandingPurchaseOrders } =
      useStockStore.getState()

    if (!purchaseOrderUi?.id) return

    const updatedPurchaseOrder =
      convertPurchaseOrderUiToPurchaseOrder(purchaseOrderUi)

    set(() => ({ isLoading: true }))
    try {
      await purchaseOrderService.updatePurchaseOrder(
        purchaseOrderUi.id,
        updatedPurchaseOrder
      )
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to update a purchase order')
    } finally {
      // If outstandingPurchaseOrders exist, replace the matching PO with the updated one to keep the list in sync.
      if (outstandingPurchaseOrders) {
        set((state) => {
          const updatedOrders = state.outstandingPurchaseOrders?.map((po) =>
            po.id === purchaseOrderUi.id ? updatedPurchaseOrder : po
          )
          return { outstandingPurchaseOrders: updatedOrders }
        })
      }

      set(() => ({ isLoading: false }))
    }
  },

  updateEstimatedCost: (updatedEstimatedCost, purchaseOrderLineId) => {
    const purchaseOrderLine = get().purchaseOrderUi?.lines?.find(
      (line) => line.lineId === purchaseOrderLineId
    )

    if (!purchaseOrderLine) return

    const updatedLine: PurchaseOrderUiLine = {
      ...purchaseOrderLine,
      unitCostOrdered: updatedEstimatedCost,
    }

    set((state) => {
      const currentUi = state.purchaseOrderUi
      const lines = currentUi?.lines ?? []

      const updatedLines = updatePurchaseOrderLine(lines, updatedLine)

      return {
        purchaseOrderUi: {
          ...currentUi,
          lines: updatedLines ?? lines,
        },
      }
    })
  },

  setEstimatedCostLoading: (purchaseOrderLineId, isLoading) => {
    const purchaseOrderLine = get().purchaseOrderUi?.lines?.find(
      (line) => line.lineId === purchaseOrderLineId
    )

    if (!purchaseOrderLine) return

    set((state) => {
      const currentUi = state.purchaseOrderUi
      const lines = currentUi?.lines ?? []

      const updatedLine = {
        ...purchaseOrderLine,
        isEstimatedCostLoading: isLoading,
      }

      const updatedLines = updatePurchaseOrderLine(lines, updatedLine)

      return {
        purchaseOrderUi: {
          ...currentUi,
          lines: updatedLines ?? lines,
        },
      }
    })
  },

  getEstimatedCost: async (
    purchaseOrderLineId,
    itemId,
    unitRatio,
    quantity
  ) => {
    const purchaseOrderLine = get().purchaseOrderUi?.lines?.find(
      (line) => line.lineId === purchaseOrderLineId
    )

    if (!purchaseOrderLine || !unitRatio || !quantity || !itemId) return

    const now = dayjs().toISOString()
    const itemIdTimeStrings = [`${itemId},${now}`]

    get().setEstimatedCostLoading(purchaseOrderLine.lineId, true)

    try {
      const stockCostData = await stockCostService.getStockCost(
        itemIdTimeStrings
      )

      // Get the first StockCost object from all itemCosts (flattened across all keys), if any exist
      const firstStockCost = Object.values(stockCostData.itemCosts ?? {})
        .flat()
        .at(0)

      get().updateEstimatedCost(
        firstStockCost?.cost ?? 0,
        purchaseOrderLine.lineId
      )
      get().setEstimatedCostLoading(purchaseOrderLine.lineId, false)
    } catch (error) {
      get().setEstimatedCostLoading(purchaseOrderLine.lineId, false)
      exceptionMessageHandler(error, 'Error fetching cost data')
    }
  },

  getOutstandingPurchaseOrders: async () => {
    try {
      const purchaseOrders =
        await purchaseOrderService.getUnreceivedPurchaseOrdersSummary()
      set(() => ({
        outstandingPurchaseOrders: orderBy(
          purchaseOrders,
          ['createdAt'],
          ['asc']
        ),
      }))
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to getOutstandingPurchaseOrders')
      set(() => ({
        outstandingPurchaseOrders: [],
      }))
    }
  },

  deletePurchaseOrder: async (purchaseOrderId) => {
    // Keep a reference to the previous state
    const previousState = get().outstandingPurchaseOrders

    try {
      // UI Optimistic update
      set((state) => ({
        outstandingPurchaseOrders: state.outstandingPurchaseOrders?.filter(
          (po) => po.id !== purchaseOrderId
        ),
      }))

      const updatedPurchaseOrder =
        await purchaseOrderService.deletePurchaseOrder(purchaseOrderId)

      enqueueLoadedSnackbar('Purchase Order Deleted', 'success')

      return updatedPurchaseOrder
    } catch (error) {
      // Rollback optimistic update
      set({ outstandingPurchaseOrders: previousState })
      exceptionMessageHandler(error)
      return null
    }
  },

  populatePurchaseOrderUi: async (purchaseOrder) => {
    const purchaseOrderUi = convertPurchaseOrderToPurchaseOrderUi(purchaseOrder)

    const { setPurchaseOrderUi } = useStockStore.getState()

    setPurchaseOrderUi(purchaseOrderUi)
  },

  getPurchaseOrderCommunicationLogs: async (purchaseOrderId) => {
    set((state) => ({
      outstandingPurchaseOrders: state.outstandingPurchaseOrders?.map((order) =>
        order.id === purchaseOrderId
          ? { ...order, communicationLogs: undefined }
          : order
      ),
    }))

    try {
      const logs = await purchaseOrderService.getCommunicationLogs(
        purchaseOrderId
      )
      const orderedLogs = orderBy(logs, ['createdAt'], ['desc'])

      // Update the specific purchase order with its communication logs
      set((state) => ({
        outstandingPurchaseOrders: state.outstandingPurchaseOrders?.map(
          (order) =>
            order.id === purchaseOrderId
              ? { ...order, communicationLogs: orderedLogs }
              : order
        ),
      }))

      return logs
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to get communication logs')

      // Set empty array for the specific purchase order on error
      set((state) => ({
        outstandingPurchaseOrders: state.outstandingPurchaseOrders?.map(
          (order) =>
            order.id === purchaseOrderId
              ? { ...order, communicationLogs: [] }
              : order
        ),
      }))

      return []
    }
  },

  startOutstandingOrdersPolling: async () => {
    // Reset polling state - clear any existing timeout and reset counter
    if (pollTimeoutId) clearTimeout(pollTimeoutId)
    pollCount = 0

    // Recursive polling function that calls itself with increasing delays
    const poll = async () => {
      try {
        // Fetch the latest outstanding purchase orders from the server
        await get().getOutstandingPurchaseOrders()
        const orders = get().outstandingPurchaseOrders ?? []

        // Find the latest sendTime for orders in "Sending" or "Sent" status
        let lastSendTime = 0
        let hasActiveOrder = false

        orders.forEach((order) => {
          if (
            order.status === PurchaseOrderStatus.Sending ||
            order.status === PurchaseOrderStatus.Sent
          ) {
            hasActiveOrder = true
            const sendTime = order.statusDateTime
              ? order.statusDateTime?.toDate().getTime()
              : 0

            if (sendTime > lastSendTime) lastSendTime = sendTime
          }
        })

        const maxDelay = pollDelays[pollDelays.length - 1]
        const now = Date.now()
        const msSinceLastSend = now - lastSendTime

        // Should poll if there is an active order AND the time since last send is less than maxDelay
        const shouldPoll =
          hasActiveOrder &&
          msSinceLastSend < maxDelay &&
          pollCount < MAX_POLL_COUNT

        if (!shouldPoll) {
          // All orders have reached final status - clean up and stop polling
          get().stopOutstandingOrdersPolling()
          return
        }

        const delay = pollDelays[Math.min(pollCount, pollDelays.length - 1)]
        pollCount += 1

        // Schedule the next poll with the calculated delay
        pollTimeoutId = setTimeout(poll, delay)
      } catch (error) {
        // Stop polling on error
        get().stopOutstandingOrdersPolling()
        exceptionMessageHandler(
          error,
          'Failed to startOutstandingOrdersPolling'
        )
      }
    }

    // Start the initial poll immediately
    poll()
  },

  stopOutstandingOrdersPolling: () => {
    // Clean shutdown of polling mechanism
    pollTimeoutId && clearTimeout(pollTimeoutId) // Cancel pending timeout
    pollTimeoutId = null // Clear reference
    pollCount = 0 // Reset counter
  },

  sendPurchaseOrderToSupplier: async (
    purchaseOrderId,
    supplierEmail,
    updateSupplierEmail,
    emailSubject,
    companyName,
    additionalMessage
  ) => {
    try {
      const response = await purchaseOrderService.sendPurchaseOrderToSupplier(
        purchaseOrderId,
        supplierEmail,
        updateSupplierEmail,
        emailSubject,
        companyName,
        additionalMessage
      )

      //We need to start polling here to get the status updates from Sendgrid
      get().startOutstandingOrdersPolling()
      updateSupplierEmail && useStockStore.getState().getSuppliers(true)

      enqueueLoadedSnackbar(
        'Email queued, see Status column for updates.',
        'success'
      )

      return response
    } catch (error) {
      exceptionMessageHandler(error, 'Sending email failed')

      // Refresh the grid to show the error status
      await get().getOutstandingPurchaseOrders()

      return undefined
    }
  },

  downloadPurchaseOrderPdf: async (purchaseOrderId) => {
    try {
      const purchaseOrderPdf = await purchaseOrderService.getPurchaseOrderPdf(
        purchaseOrderId
      )
      return purchaseOrderPdf
    } catch (error) {
      exceptionMessageHandler(error, 'Downloading PDF failed')
    }
  },

  setPurchaseOrderLoading: (isLoading) => {
    set(() => ({ isLoading }))
  },

  populatePurchaseOrderFromPredictedOrder: () => {
    const { predictedOrderUi, items, units, brands, suppliers } =
      useStockStore.getState()

    if (!predictedOrderUi?.groupedPredictedOrderLines) {
      logger.error(
        'No predicted order data available to populate purchase order'
      )
      return
    }

    const transformedLines = transformPredictedOrderLinesToPurchaseOrderLines(
      predictedOrderUi.groupedPredictedOrderLines,
      items,
      units,
      brands,
      suppliers
    )

    set(() => ({
      purchaseOrderUi: {
        lines: transformedLines,
        notes: undefined,
      },
    }))
  },
})

export default purchaseOrderSlice
